import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { resolveImage } from '../api';

/**
 * Photo carousel for activity cards.
 * - Uses `photos` (array of image URLs). Falls back to `[cover]` when only cover is present.
 * - Attribution updates based on the visible index (via `attributions[i]`).
 * - Renders dots + arrows only when photos.length > 1. Otherwise identical to a plain <img>.
 * - Independent of other card state (hours accordion, etc.). Click bubbling is
 *   stopped on the nav controls so users can browse without triggering the parent link.
 */
export default function ActivityPhotoCarousel({
  photos,
  cover,
  alt = '',
  attributions,
  testIdPrefix = 'photo',
}) {
  const list = Array.isArray(photos) && photos.length > 0 ? photos : (cover ? [cover] : []);
  const [idx, setIdx] = useState(0);

  const hasMultiple = list.length > 1;
  const clampedIdx = Math.min(idx, Math.max(0, list.length - 1));
  const currentUrl = list[clampedIdx];
  const currentAttribution = Array.isArray(attributions) ? attributions[clampedIdx] : null;

  const go = useCallback((delta) => (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setIdx((prev) => (prev + delta + list.length) % list.length);
  }, [list.length]);

  const jump = useCallback((i) => (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setIdx(i);
  }, []);

  if (!currentUrl) return null;

  return (
    <>
      <img
        src={resolveImage(currentUrl)}
        alt={alt}
        loading="lazy"
        decoding="async"
        // key forces browser to re-render on src swap so lazy attr doesn't stall
        key={clampedIdx}
        data-testid={`${testIdPrefix}-carousel-image`}
      />
      {hasMultiple && (
        <>
          {/* Arrows */}
          <button
            type="button"
            onClick={go(-1)}
            aria-label="Foto precedente"
            className="photo-carousel-arrow photo-carousel-arrow-left"
            data-testid={`${testIdPrefix}-carousel-prev`}
          >
            <ChevronLeft size={18} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={go(1)}
            aria-label="Foto successiva"
            className="photo-carousel-arrow photo-carousel-arrow-right"
            data-testid={`${testIdPrefix}-carousel-next`}
          >
            <ChevronRight size={18} strokeWidth={2.4} />
          </button>
          {/* Dots */}
          <div className="photo-carousel-dots" aria-hidden={false}>
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={jump(i)}
                className={`photo-carousel-dot ${i === clampedIdx ? 'is-active' : ''}`}
                aria-label={`Vai alla foto ${i + 1}`}
                data-testid={`${testIdPrefix}-carousel-dot-${i}`}
              />
            ))}
          </div>
        </>
      )}
      {/* Attribution — reflects the CURRENT photo, not a fixed first one */}
      {currentAttribution?.name && (
        <a
          href={currentAttribution.uri || '#'}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-black/55 text-white text-[9px] leading-tight tracking-tight backdrop-blur-sm hover:bg-black/70 transition"
          title={`Foto di ${currentAttribution.name} — via Google`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-carousel-attribution`}
        >
          © {currentAttribution.name}
        </a>
      )}
    </>
  );
}
